import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { tv } from 'tailwind-variants';

import Text from '../base/Text';
import CheckedIcon from '../icons/CheckedIcon';
import UncheckedIcon from '../icons/UncheckedIcon';

const checkboxButton = tv({
  base: 'focus-visible text-gray-90 border-gray-30 dark:border-gray-75 dark:bg-gray-90 dark:text-gray-15 relative inline-flex h-[30px] min-w-[30px] items-center gap-2 rounded-lg border bg-white transition-all duration-200 ease-in-out hover:no-underline',
  variants: {
    checkboxPlacement: {
      start: 'pl-[5px] pr-3',
      end: 'pl-3 pr-[5px]',
    },
    disabled: {
      true: 'bg-gray-30 text-gray-90 cursor-not-allowed opacity-50',
      false: '',
    },
  },
  compoundVariants: [
    {
      disabled: false,
      className:
        'hover:bg-gray-30 dark:hover:bg-gray-60 cursor-pointer active:scale-[0.98]',
    },
  ],
  defaultVariants: {
    checkboxPlacement: 'end',
    disabled: false,
  },
});

export interface CheckboxButtonProps
  extends Omit<
    ComponentPropsWithoutRef<typeof RadixCheckbox.Root>,
    'checked' | 'children' | 'onCheckedChange'
  > {
  checked: boolean;
  checkboxPlacement?: 'start' | 'end';
  children?: ReactNode;
  onCheckedChange?: (checked: boolean) => void;
}

export default function CheckboxButton({
  checked,
  checkboxPlacement = 'end',
  children,
  className,
  disabled = false,
  onCheckedChange,
  ...props
}: CheckboxButtonProps) {
  return (
    <RadixCheckbox.Root
      checked={checked}
      className={checkboxButton({ checkboxPlacement, disabled, className })}
      disabled={disabled}
      onCheckedChange={(nextChecked) => onCheckedChange?.(nextChecked === true)}
      type="button"
      {...props}
    >
      {checkboxPlacement === 'start' ? (
        <CheckboxButtonIndicator checked={checked} />
      ) : null}

      {typeof children === 'string' ? (
        <Text variant="studio/body/medium/xs" className="truncate">
          {children}
        </Text>
      ) : (
        children
      )}

      {checkboxPlacement === 'end' ? (
        <CheckboxButtonIndicator checked={checked} />
      ) : null}
    </RadixCheckbox.Root>
  );
}

function CheckboxButtonIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className="flex h-5 w-5 flex-none items-center justify-center"
    >
      {checked ? (
        <CheckedIcon className="text-blue h-5 w-5" />
      ) : (
        <UncheckedIcon className="text-gray-45 dark:text-gray-30 h-5 w-5" />
      )}
    </span>
  );
}
